import { FC, useState } from 'react';
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { CrossfadePanel } from '../../../components/motion/index';
import { StringKey } from '../../../lang/index';
import { PageRoute } from '../../../types/page.types';
import { useSpacesHome } from '../hooks/use-spaces-home.hook';
import { ActivityEmptyState } from './components/activity-empty-state';
import { ActivityOutletContext } from './types/activity-outlet-context.types';

/**
 * The four sub-tabs of Data Room Activity — 20.1.1 to 20.1.4.
 *
 * Each is its own route rather than a piece of state, so returning from a room's detail lands on
 * the list it was opened from, and a link to one tab is a link to that tab. The 7/30/90 window is
 * deliberately *not* shared between them (20.1.2 §3), which is why each tab holds its own and
 * only borrows the toolbar slot beside these buttons to show it.
 *
 * A tab stays lit on the screens opened from it: a room's detail belongs to Data Rooms and a
 * visitor's profile to Visitors.
 */
const ACTIVITY_TABS: { path: PageRoute; routes: PageRoute[]; title: StringKey }[] = [
  { path: PageRoute.DATA_ROOM_ACTIVITY, routes: [PageRoute.DATA_ROOM_ACTIVITY], title: 'OVERVIEW' },
  {
    path: PageRoute.DATA_ROOM_ACTIVITY_DATA_ROOMS,
    routes: [PageRoute.DATA_ROOM_ACTIVITY_DATA_ROOMS, PageRoute.DATA_ROOM_ACTIVITY_ROOM],
    title: 'DATA_ROOMS',
  },
  {
    path: PageRoute.DATA_ROOM_ACTIVITY_FILES,
    routes: [PageRoute.DATA_ROOM_ACTIVITY_FILES],
    title: 'FILES',
  },
  {
    path: PageRoute.DATA_ROOM_ACTIVITY_VISITORS,
    routes: [PageRoute.DATA_ROOM_ACTIVITY_VISITORS, PageRoute.DATA_ROOM_ACTIVITY_VISITOR],
    title: 'VISITORS',
  },
];

export const DataRoomActivityPage: FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [toolbar, setToolbar] = useState<HTMLDivElement | null>(null);
  // The same cached list the Workspaces home opens with. A company with no data room has nothing
  // for any tab to report, so the design drops the tabs and says so once.
  const { home } = useSpacesHome();

  if (home?.dataRoomCount === 0) {
    return <ActivityEmptyState description="NO_ACTIVITY_YET_HINT" title="NO_ACTIVITY_YET" />;
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col">
      <div className="flex w-full shrink-0 items-start justify-between gap-4 border-b border-gray-100 p-4">
        <div className="flex overflow-hidden rounded border border-gray-200">
          {ACTIVITY_TABS.map(({ path, routes, title }) => {
            const isSelected = routes.some((route) => matchPath(route, pathname));

            return (
              <Button
                // The colour shows which tab is open; this says it to a screen reader.
                aria-current={isSelected ? 'page' : undefined}
                className={twMerge(
                  'w-fit rounded-none bg-white px-4 py-2 text-sm font-[450] leading-5 text-[#858593] transition-colors duration-200 ease-out [&:not(:last-child)]:border-r [&:not(:last-child)]:border-gray-200',
                  isSelected && 'bg-brand-25 font-[550] text-[#172335]',
                )}
                key={path}
                onClick={() => navigate(path)}
                styleType="NONE"
              >
                <AppFormattedMessage id={title} />
              </Button>
            );
          })}
        </div>
        {/* Keyed by route, so each tab's controls fade in with it instead of swapping in place. */}
        <div className="animate-reveal-fade" key={pathname} ref={setToolbar} />
      </div>
      {/* Opacity only: a lingering transform on a table shows its row dividers as seams. */}
      <CrossfadePanel
        className="flex min-h-0 flex-1 flex-col"
        panelKey={pathname}
        translate={false}
      >
        <Outlet context={{ toolbar } satisfies ActivityOutletContext} />
      </CrossfadePanel>
    </div>
  );
};
